import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import Icon from '@ant-design/icons'
import { Layout as AntLayout, Menu } from 'antd'

import { User } from 'core/application/account/loginUser'
import { Permissions } from 'core/application/account/permissions'
import { UserRoles } from 'core/application/account/userRoles'

import { CompanyPersonalSection } from 'common/components/layout/CompanyPersonalComponent'
import EmployeeDashboardHeader from 'common/components/layout/DashboardHeader'
import Footer from 'common/components/layout/Footer'

import { ROUTE_PATHS } from '../common/routePaths'
import { NavigationMapping, navigationMenu, navigationPathMapping } from './index'

import './DashboardStyles.less'

const { Sider } = AntLayout

interface AdminDashboardProps {
  children?: ReactNode
  user: User
}

export interface MenuItem {
  name: string
  key: number
  path: string
  icon: string
  component: any
}

const EmployeeDashboardWrapper: React.FC<AdminDashboardProps> = ({ children, user }) => {
  let history = useHistory()
  let location = useLocation()
  const [activeKey, setActiveKey] = useState<string[] | undefined>()
  const { t: translate } = useTranslation('ee-dashboard')

  const goToPath = (path: string) => {
    history.push(path)
  }

  useEffect(() => {
    let pathname = location.pathname
    const split = pathname.split('/')[2]
    const res = navigationPathMapping.get(split)
    setActiveKey(res ? [res.toString()] : [NavigationMapping.Dashboard.toString()])
  }, [location])

  const companyPersonalSection = location.pathname.includes(ROUTE_PATHS.ADMIN_DASHBOARD_PATH)
    ? CompanyPersonalSection.Company
    : CompanyPersonalSection.Personal

  const adminDashboardMenuItems = navigationMenu
    .sort((first, second) => first.key - second.key)
    .map((item: MenuItem) => (
      <Menu.Item key={item.key} onClick={() => goToPath(item.path)}>
        <Icon type={item.icon} />
        {translate(item.name)}
      </Menu.Item>
    ))

  const getUserInfo = () => {
    return {
      firstName: user.firstName,
      lastName: user.lastName,
      isAdmin: user.roles.includes(UserRoles.CompanyAdmin) || user.roles.includes(UserRoles.CompanySecondaryAdmin),
    }
  }

  const getPersonalDashboardLink = () => {
    return user.permissions.includes(Permissions.AccessEligibleEmployeeDashboard)
      ? ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD
      : ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_DASHBOARD
  }

  const personalSectionEnabled =
    user &&
    user.permissions.some(
      (p) => p === Permissions.AccessEligibleEmployeeDashboard || p === Permissions.AccessNonEligibleEmployeeDashboard,
    )
  return (
    <div data-testid={'ineligible-dahboard'}>
      <AntLayout>
        <EmployeeDashboardHeader
          user={user}
          userInfo={getUserInfo()}
          translate={translate}
          companyDashboardLink={ROUTE_PATHS.DASHBOARD_PATH}
          personalDashboardLink={getPersonalDashboardLink()}
          companyPersonalSection={companyPersonalSection}
          personalSectionEnabled={personalSectionEnabled}
        />
        <AntLayout className="dashboard_body">
          <Sider width={240}>
            <Menu selectedKeys={activeKey} mode="inline" className="dashboard-nav">
              {adminDashboardMenuItems}
            </Menu>
          </Sider>
          <AntLayout className="dashboard-layout-content">{children}</AntLayout>
        </AntLayout>
        <Footer />
      </AntLayout>
    </div>
  )
}

export default EmployeeDashboardWrapper
